import Vue from 'vue';
import VuEx from 'vuex';
import loading from './modules/loading';
import appointments from './modules/appointments';
import availabilities from './modules/availabilities';
import error from './modules/error';
import establishment from './modules/establishment';
import places from './modules/places';
import other from './modules/other';
import resources from './modules/resources';
import services from './modules/services';
import forms from './modules/forms';
import snackbar from './modules/snackbar';
import texts from './modules/texts';
import analytics from './modules/analytics';
import stats from './modules/stats';
import { forEach, isObject, toLower, upperFirst } from 'lodash';
import router from '@/router';
import config from './../config.js';
import metadataForm from './modules/metadataForm';
import calendarDisplaySettings from './modules/calendar/calendarDisplaySettings';
import { errorHandling } from '@trimoz/trimoz-vue-error-handling';
import { jwt, kcAuth } from '@trimoz/trimoz-api-wrapper';
import gap from './modules/gap';
import mobileDialogMenu from '@/store/modules/mobileDialogMenu';
import kcLogin from '@/store/modules/kcLogin/kcLogin';
import globalSettings from '@/store/modules/globalSettings';
import { mergeLocales } from '@trimoz/forms-library';

Vue.use(VuEx);

const Store = new VuEx.Store({
    state: {
        locale: 'fr',
        localeString: 'fr-CA',
        currency: 'CAD',
        dynamicService: null,
        style: process.env.product,
        staticFilesUrl: '',
        isErrorRoute: false,
        captchaKey: process.env.VUE_APP_CAPTCHAKEY,
        theme: {},
        lastError: undefined,
        checkGA: false,
        appVersion: process.env.VUE_APP_VERSION,
        appVersionDate: process.env.VUE_APP_VERSION_DATE,
        whitelistingIp: null,
        publicPageBlocked: false,
        waitingForThemeOverride: false,
    },

    mutations: {
        UPDATE_DYNAMIC_SERVICE: (state, payload) => {
            state.dynamicService = payload;
        },
        UPDATE_STYLE: (state, product) => {
            state.style = product;
            state.staticFilesUrl = `https://static.trimoz.com/${product}`;
        },
        UPDATE_THEME: (state, payload) => {
            state.theme = payload;
        },
        UPDATE_LOCALE: (state, locale) => {
            state.locale = locale;
        },
        UPDATE_IS_ERROR_ROUTE: (state, payload) => {
            state.isErrorRoute = payload;
        },
        UPDATE_LAST_ERROR: (state, payload) => {
            state.lastError = payload;
        },
        UPDATE_API_SECURITY_WHITELISTING_IP: (state, payload) => {
            state.whitelistingIp = payload;
        },
        SET_PUBLIC_PAGE_BLOCKED: (state, payload) => {
            state.publicPageBlocked = payload;
        },
        SET_WAITING_FOR_THEME_OVERRIDE: (state, payload) => {
            state.waitingForThemeOverride = payload;
        },
    },

    getters: {
        getAppVersion: state => state.appVersion,
        getAppVersionDate: state => state.appVersionDate,
        captchaKey: state => state.captchaKey,
        getLastError: state => state.lastError,
        getEstablishmentModules: state => state.establishmentModules,
        getDynamicService: state => state.dynamicService,
        getStyle: state => state.style,
        getStaticFilesUrl: state => state.staticFilesUrl,
        locale: state => state.locale,
        invertLocale: state => state.locale === 'fr' ? 'En' : 'Fr',
        invertLocaleLong: state => state.locale === 'fr' ? 'English' : 'Français',
        isClicSante: state => state.style === 'clicsante',
        getIsErrorRoute: state => state.isErrorRoute,
        getTheme: state => state.theme,
        upperLocale: state => upperFirst(state.locale),
        getLocaleString: state => state.locale === 'fr' ? 'fr-CA' : 'en-CA',
        getCurrency: state => state.currency,
        getShowCalendar: state => state.showCalendar,
        isIpWhitelisted: state => !!state.whitelistingIp,
        getPublicPageBlocked: state => state.publicPageBlocked,
        getWaitingForThemeOverride: state => state.waitingForThemeOverride,
        getLogoUrl: state => {
            return `${state.staticFilesUrl}/images/logo-clicsante-header-v1.png`;
        },
        getAlternateLogoUrl: (state) => {
            return `${state.staticFilesUrl}/images/logo_clicsante_header_noel.png`;
        },
    },

    actions: {
        setLocale: ({ commit, state }, { i18n, locale }) => {
            const lang = toLower(locale);
            if (!lang || (lang !== 'fr' && lang !== 'en')) {
                return;
            }
            document.documentElement.setAttribute('lang', lang);
            import('@/lang/' + lang)
                .then(messages => {
                    localStorage.setItem('tmz_public_lang', lang);
                    i18n.setLocaleMessage(lang, messages.default[lang]);
                    i18n.locale = lang;
                    commit('UPDATE_LOCALE', lang);
                    mergeLocales(i18n);

                    if (isObject(router.currentRoute.meta.title)) {
                        document.title = router.currentRoute.meta.title[lang] + ' | ' + (lang === 'fr' ? 'Page publique' : 'Public page');
                    }
                });
        },
        setStyle: ({ commit }, theme) => {
            commit('UPDATE_STYLE', theme);
        },

        /**
         * @deprecated : To remove when we drop pharmaservices - use overrideCurrentTheme instead
         */
        changeCurrentTheme({ commit, getters }, product) {
            commit('UPDATE_THEME', config[product]);
            forEach(this._vm.$vuetify.theme, (value, key) => {
                this._vm.$vuetify.theme[key] = getters.getTheme[key] ? getters.getTheme[key] : value;
            });
        },

        overrideCurrentTheme({ commit, getters }, theme) {
            commit('UPDATE_THEME', theme);
            forEach(this._vm.$vuetify.theme, (value, key) => {
                this._vm.$vuetify.theme[key] = getters.getTheme[key] ? getters.getTheme[key] : value;
            });
        },

        setApiSecurityWhitelistingIp({ commit }) {
            commit('UPDATE_API_SECURITY_WHITELISTING_IP', this._vm.$api.apiSecurityWhitelistingIp());
        },
    },

    modules: {
        loading,
        appointments,
        availabilities,
        establishment,
        error,
        other,
        places,
        resources,
        services,
        snackbar,
        stats,
        texts,
        analytics,
        metadataForm,
        calendarDisplaySettings,
        errorHandling,
        jwt,
        kcAuth,
        kcLogin,
        gap,
        mobileDialogMenu,
        globalSettings,
        forms,
    },
    strict: true,
});

export default Store;
